import type { DefaultContext } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { use, useCallback } from "react";

import { UserContext } from "@/contexts/user-context";
import {
  ActiveFeedSettingsDocument,
  AllFeedsDocument,
} from "@/graphql/codegen/graphql";
import { AssociationType } from "@/utils/constants";

type GetFeedListDTO = {
  extraFilters?: Record<string, unknown>;
  context?: DefaultContext;
  extraVariables?: Record<string, unknown>;
};

export const useGetFeedList = () => {
  const { currentUserPublicKey } = use(UserContext);
  const [_getFeedListRequest, { loading: feedListLoading }] = useLazyQuery(
    AllFeedsDocument,
    {
      fetchPolicy: "network-only",
    },
  );

  const getFeedListRequest = useCallback(
    async ({ extraFilters, context, extraVariables }: GetFeedListDTO) => {
      const request = _getFeedListRequest({
        variables: {
          filter: {
            associationType: {
              equalTo: AssociationType.focusFeed,
            },
            app: {
              publicKey: {
                equalTo: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
              },
            },
            feedSubscriptionTierExists: true,
            accessGroupExists: true,
            ...extraFilters,
          },
          activeFeedSubscriptionsFilter2: {
            target: {
              publicKey: {
                equalTo: currentUserPublicKey,
              },
            },
          },
          accessGroupMembersFilter2: {
            accessGroupMemberPublicKey: {
              equalTo: currentUserPublicKey,
            },
          },
          ...extraVariables,
        },
        context,
      });
      request.retain?.();
      const { data } = await request;

      return data;
    },
    [currentUserPublicKey, _getFeedListRequest],
  );

  return {
    getFeedListRequest,
    feedListLoading,
  };
};

export const useFeedSettings = () => {
  const { currentUserPublicKey } = use(UserContext);
  const [getFeedSettings] = useLazyQuery(ActiveFeedSettingsDocument, {
    fetchPolicy: "network-only",
  });

  const getFeedSettingsRequest = useCallback(async () => {
    const request = getFeedSettings({
      variables: {
        filter: {
          associationType: {
            equalTo: AssociationType.FOCUS_FEED_ACTIVE_SUBSCRIPTION,
          },
          transactor: {
            publicKey: {
              equalTo: currentUserPublicKey,
            },
          },
        },
      },
    });
    request.retain?.();
    const { data } = await request;

    return data;
  }, [currentUserPublicKey, getFeedSettings]);

  return { getFeedSettingsRequest };
};
